// utils/momentTitle.js — Anamoria SPA
// v0.1 — Render-time title/date helpers for moment entries (Phase 3, step 3.1)
//
// Rules: moment-phase3-feed-detail-copy-v0.4.md §2 (D-3.9 as amended) and
// foundational plan §3a. Stored titles stay null — everything here is computed
// at render, nothing is written back. Shared with MomentDetailPage at 3.2.

const TYPE_NOUNS = {
  voice: 'A voice note', // copy v0.4 §2, O-2
  video: 'A video',
  photo: 'A photo',
  file: 'A document', // unreachable today — capture creates no file pieces yet (F-7)
};

const TITLE_MAX = 50;

const cat = (p) => ((p && p.category) || '').toLowerCase();

/**
 * One visual anchor per card (§3a): presence (video → voice) else photo.
 * Pieces arrive pre-sorted piece_order ASC (server, anamoria-memories v2.18),
 * so "first by pieceOrder within a type" is Array.find.
 * Returns { kind, piece } or null (text/file-only moments have no visual).
 */
export function selectAnchor(pieces = []) {
  for (const kind of ['video', 'voice', 'photo']) {
    const piece = pieces.find((p) => cat(p) === kind);
    if (piece) return { kind, piece };
  }
  return null;
}

/** First text piece by pieceOrder (server-sorted). */
export function firstTextPiece(pieces = []) {
  return pieces.find((p) => cat(p) === 'text' && p.note) || null;
}

/**
 * Copy v0.4 §2 truncation rule: cut at a word boundary, never mid-word,
 * prefer a sentence end where one falls near the limit; ellipsis if cut.
 */
export function truncateAtWordBoundary(text, max = TITLE_MAX) {
  const t = String(text || '').replace(/\s+/g, ' ').trim();
  if (t.length <= max) return t;
  const window = t.slice(0, max + 1);
  const sentenceEnd = Math.max(
    window.lastIndexOf('. '),
    window.lastIndexOf('! '),
    window.lastIndexOf('? ')
  );
  if (sentenceEnd >= Math.floor(max * 0.6)) {
    return window.slice(0, sentenceEnd + 1);
  }
  const lastSpace = window.lastIndexOf(' ');
  const cut = lastSpace > 0 ? window.slice(0, lastSpace) : t.slice(0, max);
  return `${cut}…`;
}

/** "Month Year" from the save date (copy v0.4 §2 fallback). */
export function monthYearLabel(moment) {
  const d = new Date(moment.createdAt);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

/** Card date line: entered day when present, else "Month Year" (§3a item 4). */
export function momentDateLabel(moment) {
  return moment.dayText ? moment.dayText : monthYearLabel(moment);
}

/**
 * Title per copy v0.4 §2: stored title → as-is; else text-derived (truncated,
 * never dated); else the type noun. `withDate` appends " · Month Year" to
 * GENERIC titles only, and only where the title stands alone (3.2+/search) —
 * cards pass nothing because they carry their own date line (date-duplication
 * rule, copy v0.4 §2).
 * Mixed moment with no text and no title → noun of the visual anchor's type
 * (interpretation F-8: the copy table doesn't cover mixed-no-text; the anchor
 * noun matches what the card shows).
 */
export function deriveMomentTitle(moment, { withDate = false } = {}) {
  const stored = (moment.title || '').trim();
  if (stored) return stored;
  const pieces = moment.pieces || [];
  const textPiece = firstTextPiece(pieces);
  if (textPiece) return truncateAtWordBoundary(textPiece.note);
  const anchor = selectAnchor(pieces);
  const kind = anchor ? anchor.kind : cat(pieces[0]);
  const noun = TYPE_NOUNS[kind] || 'A memory'; // defensive; D-3.12 means pieces ≥ 1
  return withDate ? `${noun} · ${monthYearLabel(moment)}` : noun;
}

/**
 * Possessive for the §1.1 empty-state headline ("This is {name}'s space.").
 * AP style [SOPHIE 2026-07-20, F-5 closed at the O-5 render check]: names
 * ending in s take a bare apostrophe ("James' space"); all others take 's.
 */
export function possessive(name) {
  const n = String(name || '').trim();
  if (!n) return '';
  return /s$/i.test(n) ? `${n}’` : `${n}’s`;
}
